import React, { useMemo } from 'react';
import { Form } from '~/shared/forms/Form';
import { useFindings } from '~/features/concepts/findings/useFindings';
import { useVisit } from '../VisitContext';
import { getDidNotPerformInterventionInitialValues, getInterventionDocumentationInitialValues } from './values';
import {
	calculateInterventionFieldValuesOptions,
	DidNotPerformReasonEnum,
	DidNotPerformReasonOptions,
	getDiagnosesOptions,
	InstructionResponseOptions,
	InterventionTaskStatusEnum,
	InterventionTypeEnum,
	PatientToleratedOptions,
	PerformedByEnum,
	PerformedByOptions,
	type InterventionTaskViewModel
} from '@generated';
import { getDidNotPerformValidationSchema, getInterventionDocumentationValidationSchema } from './validation';
import { FieldActions } from '~/libraries/forms/field-actions/FieldActions';
import type { FormValues } from '~/shared/forms/types';
import { convertClientValuesToServerValues } from '~/libraries/forms/values';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Section } from '~/shared/section/Section';
import { SectionActionButton, sectionActionButtonProps } from '~/shared/components/buttons/SectionActionButton';
import { EyeOff, Info, History, FileText } from 'lucide-react';
import { IconButtonPopover } from '~/shared/IconButtonPopover';
import { SelectFormField } from '~/shared/forms/controls/fields/SelectFormField';
import { DateTimeFormField } from '~/shared/forms/controls/fields/date-fields/DateTimeFormField';
import { TextAreaFormField } from '~/shared/forms/controls/fields/text-fields/TextAreaFormField';
import { Tag, TagGroup } from '~/shared/components/TagGroup';
import { FormFields } from '~/libraries/forms/FormFields';
import { holdUntilExternalReferencesAreResolved } from '../shared/util';
import { CheckboxFormField } from '~/shared/forms/controls/fields/CheckboxFormField';
import { TextFormField } from '~/shared/forms/controls/fields/text-fields/TextFormField';
import { Copyright } from '~/shared/Copyright';
import { SectionTitle } from '~/shared/section/SectionTitle';

export function VisitInterventionTask({
	interventionTask,
	siblingInterventionTasks
}: {
	interventionTask: InterventionTaskViewModel;
	siblingInterventionTasks: InterventionTaskViewModel[];
}) {
	const { stayId, interventionTasks, visitFindings } = useVisit();
	const [markedAsNotPerformed, setMarkedAsNotPerformed] = React.useState(
		interventionTask.interventionTaskStatusId === InterventionTaskStatusEnum.DidNotPerform
	);
	const { data: patientDiagnoses } = useSuspenseQuery(getDiagnosesOptions(stayId));
	const { findings } = useFindings(stayId);

	const diagnoses = patientDiagnoses.pending
		? patientDiagnoses.pending.diagnoses
		: patientDiagnoses.current
		? patientDiagnoses.current.diagnoses
		: [];

	const initialValues = getInterventionDocumentationInitialValues(interventionTask, interventionTasks, findings, visitFindings, diagnoses);

	const didNotPerformValues = getDidNotPerformInterventionInitialValues(interventionTask);

	const validationSchema = useMemo(() => getInterventionDocumentationValidationSchema(interventionTask), [interventionTask]);
	const didNotPerfValidationSchem = getDidNotPerformValidationSchema();

	const transformValuesForCalculation = (fieldName: string, values: FormValues) => {
		if (!interventionTask.interventionDefinition) {
			throw new Error('Intervention definition is not defined for the intervention task');
		}
		return {
			interventionDefinitionId: interventionTask.interventionDefinitionId,
			fieldName,
			values: convertClientValuesToServerValues(values, interventionTask.interventionDefinition.documentationFormFields)
		};
	};

	const filteredPerformedOptions = PerformedByOptions.filter(
		x =>
			(interventionTask.canBePerformedByPatient || x.id !== PerformedByEnum.Patient) &&
			(interventionTask.canBePerformedByCaregiver || x.id !== PerformedByEnum.Caregiver)
	);

	const siblingTaskIds = siblingInterventionTasks.map(x => x.id);
	const holdForExternalReferences = interventionTask.interventionDefinition
		? holdUntilExternalReferencesAreResolved(
				{
					...interventionTask,
					externalReferences: interventionTask.interventionDefinition.externalReferences ?? [],
					systemName: interventionTask.interventionDefinition.systemName,
					category: interventionTask.interventionDefinition.category
				},
				siblingInterventionTasks
					.filter(x => x.interventionDefinition)
					.map(x => ({
						...x,
						externalReferences: x.interventionDefinition!.externalReferences ?? [],
						systemName: x.interventionDefinition!.systemName,
						category: x.interventionDefinition!.category
					})),
				interventionTasks
					.filter(x => x.id !== interventionTask.id && !siblingTaskIds.includes(x.id) && x.interventionDefinition)
					.map(x => ({
						...x,
						externalReferences: x.interventionDefinition!.externalReferences ?? [],
						systemName: x.interventionDefinition!.systemName,
						category: x.interventionDefinition!.category
					}))
		  )
		: false;

	return (
		<Form
			key={markedAsNotPerformed.toString()}
			initialValues={markedAsNotPerformed ? didNotPerformValues : initialValues}
			validationSchema={markedAsNotPerformed ? didNotPerfValidationSchem : validationSchema}
			registryId={interventionTask.id}
			fieldCalculation={{
				calculateMutation: calculateInterventionFieldValuesOptions,
				transformValuesForCalculation
			}}
		>
			{formHelpers => {
				const didNotPerformReasonId = formHelpers.values.didNotPerformReasonId;
				return (
					<>
						<Section
							header={
								<SectionTitle
									title={interventionTask.title}
									actions={
										<div className="flex items-center gap-2">
											{interventionTask.displayDidNotPerform && (
												<SectionActionButton
													Icon={EyeOff}
													aria-label="Toggle Not Performed"
													className={
														markedAsNotPerformed
															? 'border-red-200 text-red-600 hover:border-red-300 hover:text-red-700'
															: undefined
													}
													onClick={() => setMarkedAsNotPerformed(prev => !prev)}
												/>
											)}

											<IconButtonPopover
												Icon={Info}
												aria-label="Show Info"
												{...sectionActionButtonProps}
												popoverText={
													<>
														<div className="prose max-w-sm">{interventionTask.definitionText}</div>
														{interventionTask.valuesLastUpdatedOn && (
															<div className="flex items-center gap-2 p-4 pl-0 text-base text-gray-500">
																<History className="size-4" />
																<div className="flex flex-col">
																	<div>Last updated on:</div>
																	<div>
																		{interventionTask.valuesLastUpdatedOn &&
																			interventionTask.valuesLastUpdatedOn.toLocaleString()}
																	</div>
																</div>
															</div>
														)}
													</>
												}
											/>
											<IconButtonPopover
												Icon={FileText}
												aria-label="Edit Note"
												{...sectionActionButtonProps}
												popoverText={
													<>
														{interventionTask.displayPerformedBy && (
															<SelectFormField
																name="performedById"
																options={filteredPerformedOptions}
																containerClassName="mb-2"
															/>
														)}
														<DateTimeFormField name="performedOn" granularity="minute" />
														<TextAreaFormField name="notes" label="Notes" />
													</>
												}
											/>
											<SectionActionButton
												Icon={History}
												aria-label="View History"
												onClick={() => console.log('show history')}
											/>
											{interventionTask.interventionDefinition &&
												interventionTask.interventionDefinition.flags &&
												interventionTask.interventionDefinition.flags.length > 0 && (
													<TagGroup selectionMode="none" aria-label="Documentation Flags">
														{interventionTask.interventionDefinition.flags.map(x => (
															<Tag key={x}>{x}</Tag>
														))}
													</TagGroup>
												)}
										</div>
									}
								/>
							}
							className={markedAsNotPerformed ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'}
						>
							<div className="grid grid-cols-1 gap-8">
								<VisitBucketItemTaskBody
									holdForExternalReferences={holdForExternalReferences}
									interventionTask={interventionTask}
									markedAsNotPerformed={markedAsNotPerformed}
									didNotPerformReasonId={didNotPerformReasonId}
								/>

								{interventionTask.interventionDefinition && interventionTask.interventionDefinition.copyrightExists && (
									<Copyright copyright={interventionTask.interventionDefinition.metadata['copyright']} />
								)}
							</div>
						</Section>
						{interventionTask.interventionDefinition && (
							<FieldActions formFields={interventionTask.interventionDefinition.documentationFormFields} />
						)}
					</>
				);
			}}
		</Form>
	);
}

function VisitBucketItemTaskBody({
	holdForExternalReferences,
	interventionTask,
	markedAsNotPerformed,
	didNotPerformReasonId
}: {
	holdForExternalReferences: boolean;
	interventionTask: InterventionTaskViewModel;
	markedAsNotPerformed: boolean;
	didNotPerformReasonId: string | null;
}) {
	if (holdForExternalReferences) {
		return (
			<p className="mb-4 rounded border-l-4 border-blue-500 bg-blue-50 p-4 pl-6 text-blue-700">
				This documentation references fields from other documentations:{' '}
				{interventionTask.interventionDefinition!.externalReferences.join(', ')}.
			</p>
		);
	}

	if (markedAsNotPerformed) {
		return (
			<>
				<SelectFormField name="didNotPerformReasonId" options={DidNotPerformReasonOptions} />
				{didNotPerformReasonId && Number(didNotPerformReasonId) === DidNotPerformReasonEnum.Other && (
					<TextAreaFormField name="didNotPerformReasonOther" />
				)}
			</>
		);
	}

	return (
		<>
			{interventionTask.interventionTypeId === InterventionTypeEnum.Perform && interventionTask.intervention?.templatedDefinitionText && (
				<>
					<div className="mb-4 rounded border-l-4 border-green-500 bg-green-50 p-4 pl-6 text-green-700">
						<div className="flex items-center gap-2">
							<Info size={18} />
							<div className="flex flex-col">
								<div
									dangerouslySetInnerHTML={{
										__html: interventionTask.intervention?.templatedDefinitionText
									}}
								/>
							</div>
						</div>
					</div>
					<SelectFormField name="patientToleratedId" options={PatientToleratedOptions} />
				</>
			)}
			{interventionTask.interventionDefinition && interventionTask.interventionDefinition.documentationFormControls.length > 0 ? (
				<FormFields
					formControls={interventionTask.interventionDefinition.documentationFormControls}
					formFields={interventionTask.interventionDefinition.documentationFormFields}
				/>
			) : null}
			{interventionTask.interventionTypeId === InterventionTypeEnum.Instruct && (
				<>
					<CheckboxFormField name="instructionTargetPatient" hideLabel />
					<CheckboxFormField name="instructionTargetCaregiver" hideLabel />
					<TextFormField name="instructionTargetOther" />
					<SelectFormField name="instructionResponseId" options={InstructionResponseOptions} />
				</>
			)}
		</>
	);
}
